import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { UpcomingEventsScroller } from './components/UpcomingEventsScroller';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const VideoScroller = lazy(() => import('./components/VideoScroller').then(m => ({
  default: m.VideoScroller
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NationalNetworkSection = lazy(() => import('./components/NationalNetworkSection').then(m => ({
  default: m.NationalNetworkSection
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
const VoicesOfImpactNetworkingPage = lazy(() => import('./pages/VoicesOfImpactNetworkingPage').then(m => ({
  default: m.VoicesOfImpactNetworkingPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="vw8nppi1h6lm">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="vvyw5xhcirdt">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="3koklfdxfr2zd" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="3gmh78hjdtkae" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="1w5zzehjg32n1" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="385yp6zy25x7q" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="3d2yuh8n32vxs">
      <Navbar data-id="element-7" data-mp="2zcvcwuz5fqeo" />
      <FoundingFundersPopup data-id="element-8" data-mp="2zcdbzl1jko0f" />
      <main data-id="element-9" data-mp="2zb28znfx7kad">
        <Hero data-id="element-10" data-mp="343vbs5rtwijt" />
        <UpcomingEventsScroller data-id="element-11" data-mp="343lmyliishfp" />
        <ConsularBanner data-id="element-12" data-mp="344n9bm4zkqcq" />
        <Mission data-id="element-13" data-mp="3456ioegkm6f5" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-15" data-mp="2ue21d83fa9h5" />} data-id="element-14" data-mp="2pcut77xdb89y">
          <OurPeopleStories data-id="element-16" data-mp="2ud936vij4sam" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-18" data-mp="2ge9e5mr3ubdx" />} data-id="element-17" data-mp="2ubxymth9fgig">
          <MakeDifferenceCTA data-id="element-19" data-mp="2ge0zpqfc2y0m" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-20" data-mp="2ggds6xipwqrx">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-21" data-mp="2kg4cqjhniao0" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-23" data-mp="30uvg0qqj7gm4" />} data-id="element-22" data-mp="30uc9e66c0ene">
          <VideoScroller data-id="element-24" data-mp="30t13o0tbswtn" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-26" data-mp="2vwtwy5t3vhan" />} data-id="element-25" data-mp="2vxc25rxqx5up">
          <NewsBlog data-id="element-27" data-mp="2vug320a5wb4o" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-29" data-mp="2qvwf5uc0cp9o" />} data-id="element-28" data-mp="2qwelxkwaqlvu">
          <MediaScroller data-id="element-30" data-mp="2qzak9ac23ckt" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-32" data-mp="2lx3408k71aco" />} data-id="element-31" data-mp="2lyml21dobpfi">
          <CommunityStrength data-id="element-33" data-mp="2lxuuj4yxohw3" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-35" data-mp="2hxkpbuiytvia" />} data-id="element-34" data-mp="2hx168ahkx12o">
          <MissionPanels data-id="element-36" data-mp="2hxubf70w3iip" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-38" data-mp="2d4rmxdayyz9d" />} data-id="element-37" data-mp="2d59fnzyfavcj">
          <Leadership data-id="element-39" data-mp="2d4hxprxr0w4q" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-41" data-mp="285ws1czq9k8w" />} data-id="element-40" data-mp="286gb4x146eoi">
          <AdvisoryCouncil data-id="element-42" data-mp="28781ntfutm7x" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-44" data-mp="238eltud3hj4l" />} data-id="element-43" data-mp="237vdn5d8y93r">
          <MakeDifferenceCTA data-id="element-45" data-mp="2396h142s5euc" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-47" data-mp="1y8s7fhlj5wu7" />} data-id="element-46" data-mp="1y8ad4qifhsox">
          <BusinessInvestments data-id="element-48" data-mp="1y80nx557jpka" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-50" data-mp="1u6ovkruy8q8e" />} data-id="element-49" data-mp="1u76ujw8zxik0">
          <ConsularAffairs data-id="element-51" data-mp="1u7gmvqhik1sv" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-53" data-mp="9pnysr6y2jxi" />} data-id="element-52" data-mp="9p64i03uefs8">
          <SocialMediaVoices data-id="element-54" data-mp="9ocumq9l7a5v" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-56" data-mp="dpq0h0cojdcs" />} data-id="element-55" data-mp="dq7zg4qq85oe">
          <NationalNetworkSection data-id="element-57" data-mp="doww26170zxt" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-59" data-mp="iqm5xicoj7kb" />} data-id="element-58" data-mp="ip2qftyul0jl">
          <HondurasQuiz data-id="element-60" data-mp="iqdrhm0wru70" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-62" data-mp="noxd1lblb3yu" />} data-id="element-61" data-mp="nofe2gxjmbn8">
          <ExploreHonduras data-id="element-63" data-mp="no5miowunwff" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-65" data-mp="3m72rep5zk5np" />} data-id="element-64" data-mp="3m6kx3y2vw1if">
          <FAQ data-id="element-66" data-mp="3m7w0hwsf3790" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-67" data-mp="3r54i9w9nl99q">
        <Footer data-id="element-68" data-mp="3r6fjppfmm4xo" />
      </Suspense>
      <Suspense fallback={null} data-id="element-69" data-mp="3r3bcskas0v3b">
        <ChatbaseWidget data-id="element-70" data-mp="3w4qtk05x8ffw" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'network',
  'nonprofit-directory': 'network',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'network', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  // The site opens directly on the English main page; language is switched from
  // the toggle in the navigation bar rather than a separate entry screen.
  const [showLanding, setShowLanding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showVoicesNetworking, setShowVoicesNetworking] = useState(() => window.location.hash === '#/voices-of-impact-ny-networking');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowVoicesNetworking(hash === '#/voices-of-impact-ny-networking');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    // Language switching keeps the visitor on the current page.
    setShowLanding(false);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-72" data-mp="2h5y8kcrrcbfl" />} data-id="element-71" data-mp="2h5evpagysd8f">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-73" data-mp="2h56mptnyps2i" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-74" data-mp="27e26yrqgq36m">
        <Suspense fallback={<SectionLoader data-id="element-76" data-mp="23ds1dg6l1esa" />} data-id="element-75" data-mp="23d8i9w574kco">
          <ConsularPage data-id="element-77" data-mp="23cgxzhh1u91p" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-78" data-mp="2v5hdyxt6p8o7">
        <Suspense fallback={<SectionLoader data-id="element-80" data-mp="t98hyrhvrfid" />} data-id="element-79" data-mp="t7nlgwc09mhv">
          <BusinessMapPage data-id="element-81" data-mp="ta0d617kfb84" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-82" data-mp="y8uxj7l6l7uc">
        <Suspense fallback={<SectionLoader data-id="element-84" data-mp="jg1e5b2sg7ke" />} data-id="element-83" data-mp="jhktmzgmeel4">
          <MembersDirectoryPage data-id="element-85" data-mp="ogxifpr1xvqc" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-86" data-mp="ah65lsc9uldx">
        <Suspense fallback={<SectionLoader data-id="element-88" data-mp="fh23d9udlgl1" />} data-id="element-87" data-mp="fgiv6kuj26k7">
          <VoicesOfImpactNYPage data-id="element-89" data-mp="fhadwv3109t2" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNetworking) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-90" data-mp="lcpjyzcg9re">
        <Suspense fallback={<SectionLoader data-id="element-92" data-mp="5il5ru0xm3q0" />} data-id="element-91" data-mp="5j4fingfhlsy">
          <VoicesOfImpactNetworkingPage data-id="element-93" data-mp="1ux9hru5m1jni" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94" data-mp="1zvb1mhav1n01">
        <Suspense fallback={<SectionLoader data-id="element-96" data-mp="3kdv8fqtxh849" />} data-id="element-95" data-mp="3keelat4q16bf">
          <EncuentroCatrachoMiamiPage data-id="element-97" data-mp="3kf6btpjgoduu" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-98" data-mp="3fdh0e8sbty68">
        <Suspense fallback={<SectionLoader data-id="element-100" data-mp="3u6at4vwm0asu" />} data-id="element-99" data-mp="3u5snx9ryym8s">
          <HonduranIndependenceDayPage data-id="element-101" data-mp="3pdrfy01u3hok" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-102" data-mp="6ygx9drgmaga">
        <Suspense fallback={<SectionLoader data-id="element-104" data-mp="2yohafzql20w" />} data-id="element-103" data-mp="2y6ojtca95xq">
          <NationalNetworkPage data-id="element-105" data-mp="2xdeoji120bd" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-106" data-mp="gvlgp5tx7rte">
        <Suspense fallback={<SectionLoader data-id="element-108" data-mp="bze978lvm89c" />} data-id="element-107" data-mp="bzwfyz6604vi">
          <SaveTheDatePage data-id="element-109" data-mp="2ip1d6jcow0wa" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-110" data-mp="2dqhmy6r2c2y4">
        <Suspense fallback={<SectionLoader data-id="element-112" data-mp="1rx28nxkcljnp" />} data-id="element-111" data-mp="2dsawbtt2919t">
          <MemberProfilePage slug={memberSlug} data-id="element-113" data-mp="1rvr59yutedx4" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-114" data-mp="1wu2gacwuiugx">
        <Suspense fallback={<SectionLoader data-id="element-116" data-mp="21nn5bfggoe3z" />} data-id="element-115" data-mp="21m2bxt5vv17p">
          <NewsStoryPage postId={newsPostId} data-id="element-117" data-mp="21ndmcbtu377s" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-118" data-mp="26nj7ra9ifxhf" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-119" data-mp="18yg76hkx5qj9">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-120" data-mp="1900yzzfumvdf" />
      </LanguageProvider>
    </>;
}